import { useMemo, Fragment } from "react"
import { Popover, Transition } from "@headlessui/react"
import { Column, Table } from "@tanstack/react-table"
import { SlidersHorizontal, Settings2 } from "lucide-react"
import Form from "../../../form"
import { CLASS_NAME_DEFAULT } from "../../../../constants"

function DropdownFilter({
  columnID,
  instance,
  className = CLASS_NAME_DEFAULT,
  shrinkFilter = false,
  popoverPlace = "left",
  popoverPlaceInMobile = "left"
}: {
  className?: string
  columnID: string
  instance: Table<any>
  shrinkFilter?: boolean
  popoverPlace?: "left" | "right"
  popoverPlaceInMobile?: "left" | "right"
}) {
  const column = instance.getColumn(columnID) as Column<any, unknown>

  const firstValue = instance
    .getPreFilteredRowModel()
    .flatRows[0]?.getValue(column.id)

  const columnFilterValue = column.getFilterValue()

  const facetedUniqueValues = column.getFacetedUniqueValues()

  const sortedUniqueValues = useMemo(
    () =>
      typeof firstValue === "number"
        ? []
        : Array.from(facetedUniqueValues.keys()).sort(),
    [facetedUniqueValues, firstValue]
  )

  return typeof firstValue === "number" ? (
    <div className={className}>
      <div className="flex space-x-2">
        <Form.DebouncedInput
          type="number"
          min={Number(column.getFacetedMinMaxValues()?.[0] ?? "")}
          max={Number(column.getFacetedMinMaxValues()?.[1] ?? "")}
          value={(columnFilterValue as [number, number])?.[0] ?? ""}
          onChange={value =>
            column.setFilterValue((old: [number, number]) => [value, old?.[1]])
          }
          placeholder={`Min ${
            column.getFacetedMinMaxValues()?.[0]
              ? `(${column.getFacetedMinMaxValues()?.[0]})`
              : ""
          }`}
          className="w-24 border shadow rounded"
        />
        <Form.DebouncedInput
          type="number"
          min={Number(column.getFacetedMinMaxValues()?.[0] ?? "")}
          max={Number(column.getFacetedMinMaxValues()?.[1] ?? "")}
          value={(columnFilterValue as [number, number])?.[1] ?? ""}
          onChange={value =>
            column.setFilterValue((old: [number, number]) => [old?.[0], value])
          }
          placeholder={`Max ${
            column.getFacetedMinMaxValues()?.[1]
              ? `(${column.getFacetedMinMaxValues()?.[1]})`
              : ""
          }`}
          className="w-24 border shadow rounded"
        />
      </div>
      <div className="h-1" />
    </div>
  ) : (
    <div className={className}>
      <Popover as="div" className="relative z-10 inline-block text-left">
        <Popover.Button className="group inline-flex items-center justify-center appearance-none px-3 py-2 border border-neutral-200 rounded-md placeholder-gray-400 focus:outline-none hover:bg-primary-50 hover:border-primary-500 hover:text-primary-600 sm:text-sm">
          <SlidersHorizontal
            size={16}
            className={`
              flex-shrink-0 mr-1 text-base-dark group-hover:text-primary-600 
              ${shrinkFilter ? "hidden sm:block" : ""}
            `}
            aria-hidden="true"
          />
          <Settings2
            size={16}
            className={`
              flex-shrink-0 text-base-dark group-hover:text-primary-600 my-1
              ${shrinkFilter ? "block sm:hidden" : ""}
            `}
            aria-hidden="true"
          />
          <span className={`${shrinkFilter ? "sr-only sm:not-sr-only" : ""}`}>
            Filters
          </span>
        </Popover.Button>
        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Popover.Panel
            className={`origin-top-right absolute ${
              popoverPlaceInMobile === "left"
                ? "left-0 right-auto"
                : "left-auto right-0"
            } ${
              popoverPlace === "left"
                ? "lg:left-0 lg:right-auto"
                : "lg:left-auto lg:right-0"
            } mt-2 bg-white rounded-md shadow-2xl p-4 ring-1 ring-black ring-opacity-5 focus:outline-none z-10`}
          >
            <h3 className="sr-only">Filter by {columnID}</h3>
            <div className="space-y-4">
              {sortedUniqueValues.map((value, optionIdx) => (
                <label
                  htmlFor={`filter-${columnID}-${optionIdx}`}
                  key={optionIdx}
                  className="flex items-center cursor-pointer"
                >
                  <input
                    id={`filter-${columnID}-${optionIdx}`}
                    name={`${columnID}[]`}
                    defaultValue={value as string}
                    checked={columnFilterValue === value}
                    onChange={e =>
                      column.setFilterValue(
                        columnFilterValue === value ? "" : e.target.value
                      )
                    }
                    type="checkbox"
                    className="h-4 w-4 border-gray-300 rounded text-indigo-500 focus:ring-indigo-400"
                  />
                  <span className="ml-3 pr-6 text-sm font-medium text-gray-900 whitespace-nowrap">
                    {value as string}
                  </span>
                </label>
              ))}
            </div>
          </Popover.Panel>
        </Transition>
      </Popover>
    </div>
  )
}

export default DropdownFilter
